import { LanguageCode } from '@sparted/shared-library/business/types';
import { useCallback, useMemo, useState } from 'react';

import { FilterState } from 'Libs/filter/types';
import { getLanguagesAvailableWithCode } from 'Services/language/languageService';
import { CONTENT_ID_STATUS_MAP, CONTENT_STATUS_ID_MAP } from 'Store/entities/content/content.utils';
import type { ContentFilters } from 'Store/entities/content/common/types';

import { getLanguageIds } from './utils';

export const useContentFilters = () => {
  const availableLanguages = useMemo(getLanguagesAvailableWithCode, []);

  const [storeFilters, setFilters] = useState<ContentFilters>({
    activityIds: [],
    languageCodes: [],
    status: null,
    thematicId: 0,
    userIds: [],
    archived: false,
    queries: [],
  });
  const isFiltering = useMemo(() => {
    return Object.values(storeFilters).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return !!value;
    });
  }, [storeFilters]);

  const filters = useMemo<FilterState[]>(
    () => [
      {
        category: 'language',
        ids: getLanguageIds(storeFilters.languageCodes, availableLanguages),
      },
      {
        category: 'campaign',
        ids: [...storeFilters.activityIds],
      },
      {
        category: 'contributor',
        ids: [...storeFilters.userIds],
      },
      {
        category: 'thematic',
        ids: storeFilters.thematicId ? [storeFilters.thematicId] : [],
      },
      {
        category: 'contentStatus',
        ids: storeFilters.status ? [CONTENT_STATUS_ID_MAP[storeFilters.status]] : [],
      },
      {
        category: 'text',
        values: [],
      },
    ],
    [storeFilters, availableLanguages],
  );

  const languages = useMemo(getLanguagesAvailableWithCode, []);
  const formatFiltersForStore = useCallback(
    (filterValues: FilterState[]) =>
      filterValues.reduce<ContentFilters>(
        (acc: ContentFilters, filter: FilterState) => {
          if (filter.category === 'contentStatus') {
            const archived = filter.ids.includes(CONTENT_STATUS_ID_MAP.archived);

            let status = null;
            if (filter.ids.length > 0) {
              const statusValue = CONTENT_ID_STATUS_MAP[filter.ids[0]];
              status = statusValue !== 'archived' ? statusValue : null;
            }

            return {
              ...acc,
              status,
              archived,
            };
          }

          if (filter.category === 'language') {
            const languageCodes = filter.ids
              .map((id) => {
                const languageInfo = languages.find((lang) => lang.id === id);
                if (languageInfo) {
                  return languageInfo.languageCode;
                }
                return;
              })
              .filter((languageCode): languageCode is LanguageCode => Boolean(languageCode));

            return {
              ...acc,
              languageCodes,
            };
          }

          if (filter.category === 'thematic') {
            return { ...acc, thematicId: filter.ids.length > 0 ? filter.ids[0] : null };
          }

          if (filter.category === 'contributor') {
            return { ...acc, userIds: filter.ids };
          }

          if (filter.category === 'campaign') {
            return { ...acc, activityIds: filter.ids };
          }

          if (filter.category === 'text') {
            return { ...acc, queries: filter.values };
          }

          return acc;
        },
        {
          activityIds: [],
          languageCodes: [],
          status: null,
          thematicId: null,
          userIds: [],
          archived: false,
          queries: [],
        },
      ),
    [],
  );

  return { isFiltering, filters, storeFilters, setFilters, formatFiltersForStore };
};
